import React, { useEffect, useState } from "react";
import { FaBars, FaTimes, FaFacebookF, FaInstagram } from "react-icons/fa";
import { FaLocationDot, FaPhone, FaWhatsapp } from "react-icons/fa6";
import Logo from "./Logo";
import { site, addressLine, whatsappUrl, mapsUrl, navLinks } from "../data/site";
import { trackEvent } from "../lib/analytics";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Con el menú móvil abierto bloqueamos el scroll del fondo y habilitamos Escape.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => e.key === "Escape" && setOpen(false);
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Barra superior de contacto — sólo desktop */}
      <div className="hidden bg-campo-950 text-campo-100 lg:block">
        <div className="container-site flex items-center justify-between py-2 text-xs">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 transition-colors hover:text-white"
          >
            <FaLocationDot className="text-campo-400" aria-hidden="true" />
            {addressLine}
          </a>
          <div className="flex items-center gap-6">
            <a
              href={`tel:+${site.phoneRaw}`}
              onClick={() => trackEvent("contact_click", { method: "phone", location: "topbar" })}
              className="flex items-center gap-2 transition-colors hover:text-white"
            >
              <FaPhone className="text-campo-400" aria-hidden="true" />
              {site.phoneDisplay}
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("contact_click", { method: "whatsapp", location: "topbar" })}
              className="flex items-center gap-2 transition-colors hover:text-white"
            >
              <FaWhatsapp className="text-green-400" aria-hidden="true" />
              WhatsApp
            </a>
            <span className="h-3 w-px bg-white/20" aria-hidden="true" />
            <div className="flex items-center gap-3">
              <a
                href={site.social.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook de Riego Mercedes"
                className="transition-colors hover:text-white"
              >
                <FaFacebookF />
              </a>
              <a
                href={site.social.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram de Riego Mercedes"
                className="transition-colors hover:text-white"
              >
                <FaInstagram />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Navegación principal */}
      <nav
        aria-label="Navegación principal"
        className={`transition-all duration-300 ${
          scrolled
            ? "bg-white/95 shadow-md backdrop-blur-md"
            : "bg-white lg:bg-white/95 lg:backdrop-blur-md"
        }`}
      >
        <div className="container-site flex h-[68px] items-center justify-between">
          <Logo />

          <ul className="hidden items-center gap-1 lg:flex">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="rounded-md px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-campo-50 hover:text-campo-700"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <a
              href="#contacto"
              onClick={() => trackEvent("cta_click", { location: "navbar" })}
              className="btn-primary hidden sm:inline-flex"
            >
              Pedir cotización
            </a>

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Abrir menú"
              aria-expanded={open}
              aria-controls="menu-movil"
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-campo-900 transition-colors hover:bg-campo-50 lg:hidden"
            >
              <FaBars size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      {/* Menú móvil */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          open ? "visible" : "invisible"
        }`}
        aria-hidden={!open}
      >
        <button
          type="button"
          tabIndex={open ? 0 : -1}
          aria-label="Cerrar menú"
          onClick={() => setOpen(false)}
          className={`absolute inset-0 h-full w-full cursor-default bg-campo-950/60 backdrop-blur-sm transition-opacity duration-300 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />

        <div
          id="menu-movil"
          className={`absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <Logo />
            <button
              type="button"
              tabIndex={open ? 0 : -1}
              onClick={() => setOpen(false)}
              aria-label="Cerrar menú"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-campo-900 transition-colors hover:bg-campo-50"
            >
              <FaTimes size={18} aria-hidden="true" />
            </button>
          </div>

          <ul className="flex-1 overflow-y-auto px-3 py-4">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  tabIndex={open ? 0 : -1}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-4 py-3 font-display text-base font-semibold text-campo-950 transition-colors hover:bg-campo-50"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="space-y-3 border-t border-slate-100 p-5">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              tabIndex={open ? 0 : -1}
              onClick={() => trackEvent("contact_click", { method: "whatsapp", location: "mobile_menu" })}
              className="btn w-full bg-[#25D366] text-white hover:brightness-95"
            >
              <FaWhatsapp size={18} aria-hidden="true" />
              Escribinos por WhatsApp
            </a>
            <a
              href={`tel:+${site.phoneRaw}`}
              tabIndex={open ? 0 : -1}
              onClick={() => trackEvent("contact_click", { method: "phone", location: "mobile_menu" })}
              className="btn w-full border border-slate-200 text-campo-900 hover:bg-campo-50"
            >
              <FaPhone size={15} aria-hidden="true" />
              {site.phoneDisplay}
            </a>
            <p className="pt-1 text-center text-xs leading-relaxed text-slate-500">
              {addressLine}
            </p>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
